<template>
    <div>
        <div v-if="isWaitingForBlacklist" class="overlay">
            <ProgressSpinner style="width: 50px; height: 50px" aria-label="blacklist spinner"
                             :pt="{
                    spinner: { style: { width: '50px', height: '50px', animationDuration: '.5s' } },
                    circle: { style: { stroke: '#007bff', strokeWidth: 4, animation: '2s' } }
                }"
            />
        </div>
        <div>
            <h1 class="page-title">Liste des utilisateurs actifs</h1>
        </div>
        <ChangeBlacklistStateModal :historique-blacklist="editDialogData" :allow-blacklist-only="true" v-model:visible="showEditDialog" />
        <UserBlackListModal :user-uuid="createDialogData" v-model:visible="showCreateDialog" />
        <DataTable
            lazy
            paginator
            removableSort
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            :value="tableData"
            :rows="rows"
            :totalRecords="totalRecords"
            :rowsPerPageOptions="rowsPerPageOptions"
            :currentPage="currentPage"
            :rowHover="true"
            :loading="isLoading"
            @sort="onSort($event)"
            @page="onPaginate"
        >
            <template #header>
                <div class="flex justify-between items-center my-1">
                    <span class="p-input-icon-left">
                    <i class="pi pi-search" />
                    <InputText
                        type="text"
                        class="form-control w-64 pl-10 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        @keyup.enter="onFilterDt"
                        placeholder="Rechercher..."
                        v-model="criteria"
                        @input="onClearInputFilter"
                    />
                    </span>
                   
                </div>
                <div class="flex justify-end ml-auto ">Utilisateurs actifs : &nbsp; <b> {{ totalRecords }}</b> </div>
            </template>
            
            <div>
                <Column headerStyle="width: 3rem" />
                <Column field="firstname" header="Nom">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.firstname }}</span>
                    </template>
                </Column>
                <Column field="lastname" header="Prénom">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.lastname }}</span>
                    </template>
                </Column>
                <Column field="email" header="Email">
                    <template #body="slotProps">
                        <span>{{ slotProps.data.email }}</span>
                    </template>
                </Column>
                <Column header="Action">
                    <template #body="slotProps">

                        <Button
                            size="small"
                            icon="pi pi-ban"
                            class="p-button-danger"
                            title="Blacklister"
                            @click="redirectBlackListModal(slotProps.data)" />
                        
                    </template>
                </Column>
            </div>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from "vue";
import {UserBasicRepresentation} from "@/models/user/UserBasicRepresentation";
import {getUser, getUsers} from "@/_services/user/user";
import ChangeBlacklistStateModal from "@/components/modal/blacklisting/ChangeBlacklistStateModal.vue";
import UserBlackListModal from "@/components/modal/blacklisting/UserBlackListModal.vue";
import {getUserHistoriqueBlackList} from "@/_services/blackList/blacklist";
import {UserBlacklistHistorique} from "@/models/blacklisting/UserBlacklistHistorique";
import ProgressSpinner from "primevue/progressspinner";
import Button from "primevue/button";

const isLoading = ref<boolean>(true);
const isWaitingForBlacklist = ref<boolean>(false);
const users = Array<UserBasicRepresentation>();
const tableData = ref<Array<UserBasicRepresentation>>(users);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const currentPage = ref<number>(0);
const criteria = ref<string>("");
const modeSortable = ref<boolean>(false);
const defaultsSort = {
    fieldLabel: "order[id]",
    fieldValue: "asc",
};
const showEditDialog = ref(false);
const showCreateDialog = ref(false);
const editDialogData = ref<UserBlacklistHistorique>({} as UserBlacklistHistorique);
const createDialogData = ref('');

onMounted(async () => {
    await populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
});

const populateDatatable = async (
    itemsPerPage: number,
    page: number,
    criteria?:string,
    sort?: { fieldLabel: string; fieldValue: string; }
) => {
    const [isLoadingDt, totalRecordsDt, tableDataDt] = await getUsers(itemsPerPage, page, 'accepted', true, criteria, sort);
    isLoading.value = isLoadingDt;
    totalRecords.value = totalRecordsDt;
    tableData.value = tableDataDt;
};

const onPaginate = async (e) => {
    rows.value = e.rows;
    populateDatatable(e.rows, e.page + 1, criteria.value, defaultsSort);
};

const onFilterDt = () => {
    if (criteria.value != "") {
        populateDatatable(rows.value, page.value, criteria.value);
    }
};

const onClearInputFilter = () => {
    if (criteria.value === "") {
        populateDatatable(rows.value, page.value);
    }
};

const onSort = (e) => {
    if (e.sortField) {
        const sort = {
            fieldLabel: "order[" + e.sortField + "]",
            fieldValue: e.sortOrder === 1 ? "asc" : "desc",
        };
        modeSortable.value = true;
        populateDatatable(rows.value, page.value, criteria.value, sort);
    } else {
        modeSortable.value = false;
        populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
    }
};

const redirectBlackListModal = async(user: UserBasicRepresentation) => {
    isWaitingForBlacklist.value = true;
    // if user has already been blocked in the past he's save in ce_user_blacklist table
    if (user.userBlackList === null) {
        createDialogData.value = user.uuid;
        showCreateDialog.value = true;
    }
    if (user.userBlackList !== null) {
        const [isLoadingDt, userHistoriqueBlackList] = await getUserHistoriqueBlackList(user.userBlackList);
        editDialogData.value = userHistoriqueBlackList;
        showEditDialog.value = true;
    }

    isWaitingForBlacklist.value = false;
};
</script>